import { PropsWithChildren, FC, useState, useMemo, useCallback } from "react";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useSearchInput } from "@jugl-web/utils";
import { BoardTemplatesModalContext } from "./BoardTemplatesModalContext";

export const BoardTemplatesModalProvider: FC<
  PropsWithChildren<{
    entityId: string;
    onUseTemplateSuccess: (id: string) => void;
    onClose?: () => void;
  }>
> = ({ entityId, children, onUseTemplateSuccess, onClose }) => {
  const { tasksApi } = useRestApiProvider();
  const [createBoardFromTemplate, { isLoading: isCreating }] =
    tasksApi.useCreateBoardFromTemplateMutation();
  const [selectedCategory, setSelectedCategory] =
    useState<string>("popular-templates");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const {
    searchQuery,
    inputProps: searchInputProps,
    reset: resetSearchQuery,
  } = useSearchInput();
  const handleSelectCategory = useCallback(
    (category: string) => {
      setSelectedCategory(category);
      setSelectedTemplate(null);
      resetSearchQuery();
    },
    [resetSearchQuery]
  );

  const handleUseTemplate = useCallback(
    async (templateId: string) => {
      const response = await createBoardFromTemplate({
        id: templateId,
        entityId: entityId || "",
      });
      if ("data" in response && response.data?.id) {
        onUseTemplateSuccess(response.data.id);
        resetSearchQuery();
        setSelectedCategory("popular-templates");
        setSelectedTemplate(null);
      }
      onClose?.();
    },
    [
      createBoardFromTemplate,
      entityId,
      onClose,
      onUseTemplateSuccess,
      resetSearchQuery,
    ]
  );

  const value = useMemo(
    () => ({
      selectedCategory,
      selectedTemplate,
      isCreatingBoardInProgress: isCreating,
      searchQuery,
      searchInputProps,
      resetSearchQuery,
      handleUseTemplate,
      setSelectedCategory: handleSelectCategory,
      setSelectedTemplate,
    }),
    [
      handleSelectCategory,
      handleUseTemplate,
      isCreating,
      searchInputProps,
      resetSearchQuery,
      searchQuery,
      selectedCategory,
      selectedTemplate,
    ]
  );

  return (
    <BoardTemplatesModalContext.Provider value={value}>
      {children}
    </BoardTemplatesModalContext.Provider>
  );
};
